import { SlideObject, isBodyFont, resolvedFontWeight } from '../types';
import { detectListKind, ListKind } from './listHtml';

export type TextStyleStep = {
  id: 'heading' | 'subheading' | 'body' | 'bullets';
  label: string;
  fontFamily: string;
  fontSize: number;
  fontWeight: number;
  textTransform: 'uppercase' | 'none';
  lineHeight: number;
  list: ListKind;
};

export function stripLayerText(html?: string): string {
  return String(html || '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
}

export function layerDisplayName(object: SlideObject): string {
  const named = (object.name || '').trim();
  if (named) return named;
  if (object.type === 'text') {
    const text = stripLayerText(object.content);
    if (!text) return 'Text';
    return text.length > 24 ? `${text.slice(0, 24)}…` : text;
  }
  if (object.type === 'image') return 'Bild';
  if (object.type === 'product') return 'Produkt';
  if (object.type === 'table') {
    const text = stripLayerText(object.table?.rows?.[0]?.cells?.[0]?.content);
    return text ? (text.length > 24 ? `${text.slice(0, 24)}…` : text) : 'Tabell';
  }
  if (object.type === 'chart') {
    const name = (object.chart?.series?.[0]?.name || '').trim();
    return name ? (name.length > 24 ? `${name.slice(0, 24)}…` : name) : 'Diagram';
  }
  if (object.style?.shape === 'line') return 'Linje';
  if (object.style?.shape === 'ellipse') return 'Ellips';
  if (object.style?.shape === 'triangle') return 'Triangel';
  if (object.style?.shape === 'roundRect') return 'Rundad';
  return 'Form';
}

export function layersFrontToBack<T extends { z: number }>(objects: T[]): T[] {
  return [...objects].sort((a, b) => b.z - a.z);
}

/** `orderedIds` is front-to-back (highest z first), matching the layers list. */
export function applyLayerOrder<T extends { id: string; z: number }>(objects: T[], orderedIds: string[]): T[] {
  const zById = new Map(orderedIds.map((id, index) => [id, orderedIds.length - index]));
  return objects.map((object) => {
    const z = zById.get(object.id);
    return z == null ? object : { ...object, z };
  });
}

export const TEXT_STYLE_STEPS: TextStyleStep[] = [
  { id: 'heading', label: 'Rubrik', fontFamily: 'Kapra Neue', fontSize: 96, fontWeight: 600, textTransform: 'uppercase', lineHeight: 1, list: 'none' },
  { id: 'subheading', label: 'Liten rubrik', fontFamily: 'Kapra Neue', fontSize: 48, fontWeight: 600, textTransform: 'uppercase', lineHeight: 1.05, list: 'none' },
  { id: 'body', label: 'Brödtext', fontFamily: 'Akkurat Std', fontSize: 32, fontWeight: 300, textTransform: 'none', lineHeight: 1.1, list: 'none' },
  { id: 'bullets', label: 'Punkter', fontFamily: 'Akkurat Std', fontSize: 31, fontWeight: 300, textTransform: 'none', lineHeight: 1.25, list: 'bullet' },
];

export function textStylePatch(step: TextStyleStep): Record<string, unknown> {
  return {
    fontFamily: step.fontFamily,
    fontSize: step.fontSize,
    fontWeight: step.fontWeight,
    textTransform: step.textTransform,
    lineHeight: step.lineHeight,
  };
}

export function textStyleStepsFromSettings(steps?: Array<{
  id: TextStyleStep['id'];
  label: string;
  fontFamily: string;
  fontSize: number;
  fontWeight: number;
  textTransform: 'none' | 'uppercase';
  lineHeight: number;
  list: ListKind;
}>): TextStyleStep[] {
  if (!steps?.length) return TEXT_STYLE_STEPS;
  return steps.map((step) => ({
    id: step.id,
    label: step.label,
    fontFamily: step.fontFamily,
    fontSize: step.fontSize,
    fontWeight: step.fontWeight,
    textTransform: step.textTransform,
    lineHeight: step.lineHeight,
    list: step.list,
  }));
}

export function matchTextStyleStep(
  style?: SlideObject['style'],
  content?: string,
  steps: TextStyleStep[] = TEXT_STYLE_STEPS,
): TextStyleStep | null {
  const list = detectListKind(content);
  return steps.find((step) => {
    if (step.list === 'bullet') return list === 'bullet' && style?.fontSize === step.fontSize;
    if (list === 'bullet') return false;
    const familyMatch = step.fontFamily === 'Akkurat Std'
      ? isBodyFont(style?.fontFamily)
      : style?.fontFamily === step.fontFamily;
    return familyMatch
      && style?.fontSize === step.fontSize
      && resolvedFontWeight(style) === step.fontWeight;
  }) || null;
}
